<template>
  <SPassengerInfo
    :appConfig="appConfig"
    :isAuth="getIsAuth"
    :isFreeTrip="getIsFreeTrip"
    :customerDetails="getUserDetailsForBookingDetails"
    :passengerPhone="getPassengerPhone"
    :passengerFirstName="getPassengerFirstName"
    :passengerLastName="getPassengerLastName"
    :passengerEmail="getPassengerEmail"
    :isAnotherPassenger="isAnotherPassenger"
    :isRideToOrFromAirport="isRideToOrFromAirport"
    :flightDetails="getFlightDetails"
    :paymentType="getPaymentType"
    :paymentMethod="getPaymentMethod"
    :isOnBoardPaymentAllowed="isOnBoardPaymentAllowed"
    :commentForOutward="getCommentForOutward"
    :commentForReturn="getCommentForReturn"
    :tripType="getTripType"
    :lang="this.$route.params.lang"
    :anotherPassengersList="getAnotherPassengerList"
    :opsZoneCountry="getCountry"
    :userLocation="getLocation"
    termsAndConditionsPageName="terms-and-conditions"
    privacyPolicyPageName="https://www.slg.lu/en/politique-de-confidentialite/"
    @updatePhone="updatePhone"
    @updateFirstName="updateFirstName"
    @updateLastName="updateLastName"
    @updateEmail="updateEmail"
    @updatePassenger="updatePassenger"
    @updateFlightDetails="updateFlightDetails"
    @updatePaymentType="updatePaymentType"
    @updatePaymentMethod="updatePaymentMethod"
    @updateCommentForOutward="updateCommentForOutward"
    @updateCommentForReturn="updateCommentForReturn"
    @populateUserProfileInfo="populateUserProfileInfo"
    @setAuthToken="setAuthToken"
    @setGlobalLoading="setGlobalLoading"
    @trackGtmEventForRegisteredUser="onTrackEventForRegisteredUser"
    @trackGtmEventForNonRegisteredUser="onTrackEventForNonRegisteredUser"
    @trackGtmEventForUserCreatedByOperator="onTrackEventForUserCreatedByOperator"
  />
</template>

<script lang="ts">
export default {
  name: 'PassengerInfo',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { SPassengerInfo } from '@slg/web-customer-shared';
import importedAppConfig from '@/appConfig';
import { useStore, useGlobalErrorDialog, useGtm } from '@/composables';

const emit = defineEmits(['onSignInSuccess']);
const store = useStore();
const { setGlobalError } = useGlobalErrorDialog();
const { trackGtmEvent } = useGtm();

const appConfig = ref<Record<string, any>>({});
appConfig.value = importedAppConfig;

const getIsAuth = computed(() => store.getters['auth/getIsAuth']);
const getUserDetailsForBookingDetails = computed(() => store.getters['auth/getUserDetailsForBookingDetails']);
const getAnotherPassengerList = computed(() => store.getters['auth/getAnotherPassengerList']);
const getPassengerPhone = computed(() => store.getters['booking/getPassengerPhone']);
const getPassengerFirstName = computed(() => store.getters['booking/getPassengerFirstName']);
const getPassengerLastName = computed(() => store.getters['booking/getPassengerLastName']);
const getPassengerEmail = computed(() => store.getters['booking/getPassengerEmail']);
const isAnotherPassenger = computed(() => store.getters['booking/isAnotherPassenger']);
const isRideToOrFromAirport = computed(() => store.getters['booking/isRideToOrFromAirport']);
const getFlightDetails = computed(() => store.getters['booking/getFlightDetails']);
const getPaymentType = computed(() => store.getters['booking/getPaymentType']);
const getPaymentMethod = computed(() => store.getters['booking/getPaymentMethod']);
const isOnBoardPaymentAllowed = computed(() => store.getters['opsZone/isOnBoardPaymentAllowed']);
const getTripType = computed(() => store.getters['booking/getTripType']);
const getCommentForOutward = computed(() => store.getters['booking/getCommentForOutward']);
const getCommentForReturn = computed(() => store.getters['booking/getCommentForReturn']);
const getIsFreeTrip = computed(() => store.getters['booking/getIsFreeTrip']);
const getCountry = computed(() => store.getters['opsZone/getCountry']);
const getLocation = computed(() => store.getters['auth/getLocation']);

function updatePhone(newValue: string) {
  if (!newValue?.includes('undefined')) {
    store.commit('booking/setPassengerPhone', newValue);
  }
}

function updateFirstName(newValue: string) {
  store.commit('booking/setPassengerFirstName', newValue);
}

function updateLastName(newValue: string) {
  store.commit('booking/setPassengerLastName', newValue);
}

function updateEmail(newValue: string) {
  store.commit('booking/setPassengerEmail', newValue);
}

function updatePassenger(newValue: boolean) {
  if (newValue) {
    store.commit('booking/setAnotherPassenger', newValue);
  } else {
    store.commit('booking/setLoggedInPassengerDetails', getUserDetailsForBookingDetails.value);
  }
}

function updateFlightDetails(newValue: string) {
  store.commit('booking/setFlightDetails', newValue);
}

function updatePaymentType(newValue: string) {
  store.commit('booking/setPaymentType', newValue);
}

function updatePaymentMethod(newValue: string) {
  store.commit('booking/setPaymentMethod', newValue);
}

function updateCommentForOutward(newValue: string) {
  store.commit('booking/setCommentForOutward', newValue);
}

function updateCommentForReturn(newValue: string) {
  store.commit('booking/setCommentForReturn', newValue);
}

function setGlobalLoading(isLoading: boolean) {
  store.commit('common/setGlobalLoading', isLoading);
}

function setAuthToken(authToken: string) {
  if (localStorage.getItem('authToken') !== authToken) {
    store.commit('auth/clearAuthTokenAndProfileInfo');
    store.commit('auth/setAuthToken', authToken);
  }
}

async function populateUserProfileInfo() {
  try {
    await store.dispatch('auth/populateUserProfileInfo');
    emit('onSignInSuccess');
  } catch (e) {
    await setGlobalError(e as any);
  }
}

function onTrackEventForRegisteredUser() {
  trackGtmEvent('Checkout auth next button for registered user click', 'Button', 'Click', 'Next step for registered user');
}

function onTrackEventForNonRegisteredUser() {
  trackGtmEvent('Checkout auth next button for non registered user click', 'Button', 'Click', 'Next step for non-registered user');
}

function onTrackEventForUserCreatedByOperator() {
  trackGtmEvent(
    'Checkout auth next button for registered user created by operator click',
    'Button',
    'Click',
    'Next step for created by operator user',
  );
}
</script>
